import { miniAppBridgeService } from "@/api/services/miniapp-bridge-service";
import useUIPreferencesStore from "@/store/use-ui-preferences-store";
import { getUILanguageName, getUILocale } from "@/i18n/languages";

const applyLanguage = (language: string) => {
  // Store, i18next ve kalıcı dil aynı giriş yolundan güncellenir.
  useUIPreferencesStore.getState().setLanguage(getUILanguageName(language));
};

/**
 * Set user language on API
 *
 * MiniApp içinde ilk defa açıldığında kullanıcının dilini API'de de set eder
 */
const setUserLanguageOnAPI = async (language: string) => {
  try {
    const { apiClient } = await import("@/api/config/api-client");
    const { API_ENDPOINTS } = await import("@/api/config/api-endpoints");
    const useAuthStoreModule = await import("@/store/use-auth-store");
    const useAuthStore = useAuthStoreModule.default;

    const user = useAuthStore.getState().user;

    if (!user) {
      console.warn(
        "🌍 [i18n WebView] No user found, skipping API language update"
      );
      return;
    }

    // API'ye dil değişikliği gönder
    await apiClient.put(API_ENDPOINTS.updateProfile, {
      language: getUILanguageName(language),
    });

    console.log(
      `🌍 [i18n WebView] Language set to ${language} on API successfully`
    );
  } catch (error) {
    console.error("🌍 [i18n WebView] Failed to set language on API:", error);
  }
};

export const initializeLanguageForWebView = () => {
  // 1. URL Query Parameter Kontrolü
  const urlParams = new URLSearchParams(window.location.search);
  const langFromUrl = urlParams.get("lang");

  if (langFromUrl) {
    applyLanguage(langFromUrl);
  }

  // 2. MiniApp Default Language Check
  const isInMiniApp = miniAppBridgeService.isInMiniApp();
  if (isInMiniApp && !langFromUrl) {
    // Check if language is already set in localStorage
    const storedLanguage = localStorage.getItem("i18nextLng");

    if (!storedLanguage) {
      // Default to Turkish for MiniApp
      applyLanguage("tr");

      // API'de de Türkçe olarak set et
      setUserLanguageOnAPI("tr");
    } else {
      applyLanguage(storedLanguage);
    }
  }

  // 3. postMessage Listener - Super app'ten gelen mesajları dinle
  const handleMessage = (event: MessageEvent) => {
    // Security: Origin kontrolü yapılabilir
    // if (event.origin !== 'https://trusted-super-app.com') return;

    if (event.data?.type === "SET_LANGUAGE" && typeof event.data.language === "string") {
      const language = event.data.language;

      applyLanguage(language);
    }
  };

  window.addEventListener("message", handleMessage);

  // 4. Super app'e hazır olduğunu bildir
  // Super app bu mesajı alınca dil bilgisini gönderebilir
  if (window.parent !== window) {
    window.parent.postMessage({ type: "WEBVIEW_READY" }, "*");
  }

  // Cleanup function
  return () => {
    window.removeEventListener("message", handleMessage);
  };
};

/**
 * WebView Detection Utility
 *
 * Uygulamanın webview içinde mi yoksa normal tarayıcıda mı çalıştığını tespit eder
 */
export const isRunningInWebView = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();

  // Yaygın webview user agent pattern'leri
  const webViewPatterns = [
    "wv", // Android WebView
    "webview",
    "harmonyapp", // Super app'iniz için özel pattern ekleyebilirsiniz
  ];

  return webViewPatterns.some((pattern) => userAgent.includes(pattern));
};

/**
 * Language Code Mapper
 *
 * Super app'ten gelen dil kodlarını i18n formatına çevirir
 */
export const mapLanguageCode = (code: string): string => {
  return getUILocale(code);
};
